import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Form, Table, Modal, Tabs, Tab } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { StyledCard, StyledBody, PreviewContainer } from './styles';
import { StyledButton } from 'components/forms/Buttons';
import {
  RawTermSettingValue,
  CategorySetting,
  SettingsCardProps,
} from 'types/settings';
import { termsAPI } from 'helpers/http/settingApi';

interface TermFormData {
  setting_key: string;
  setting_value: RawTermSettingValue;
  description: string;
}

interface TermsCardProps extends SettingsCardProps {
  resetFormState?: () => void;
}

const DEFAULT_FORM_DATA: TermFormData = {
  setting_key: '',
  setting_value: {
    title: '',
    content: '',
    html: '',
  },
  description: '',
};

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean'],
    [{ color: [] }, { background: [] }],
    ['code-block'],
  ],
};

const EDITOR_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'color',
  'background',
  'code-block',
];

const TermsCard = forwardRef<{ resetFormState: () => void }, TermsCardProps>(
  ({ showModal, setShowModal, onCloseHandler }, ref) => {
    const [search, setSearch] = useState('');
    const [selectedSetting, setSelectedSetting] = useState<CategorySetting<
      RawTermSettingValue,
      'terms'
    > | null>(null);
    const [previewTab, setPreviewTab] = useState('edit');
    const [formData, setFormData] = useState<TermFormData>(DEFAULT_FORM_DATA);

    const queryClient = useQueryClient();

    const resetFormState = () => {
      setSelectedSetting(null);
      setFormData(DEFAULT_FORM_DATA);
      setPreviewTab('edit');
    };

    useImperativeHandle(ref, () => ({
      resetFormState,
    }));

    useEffect(() => {
      if (showModal && !selectedSetting) {
        resetFormState();
      }
    }, [showModal, selectedSetting]);

    const { data: settings, isLoading: isLoadingSettings } = useQuery(
      ['settings', 'terms', search],
      () => termsAPI.getSettings(search),
      {
        select: (response) => response.data,
        keepPreviousData: true,
      }
    );

    const addSettingMutation = useMutation(
      (data: TermFormData) =>
        termsAPI.addSetting(data.setting_key, data.setting_value, {
          description: data.description,
        }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['settings']);
          toast.success('Terms added successfully');
          onCloseHandler('terms');
          resetFormState();
        },
        onError: (error: any) => {
          toast.error(error.message || 'Failed to add terms');
        },
      }
    );

    const editSettingMutation = useMutation(
      (data: {
        setting_id: number;
        setting_key: string;
        setting_value: RawTermSettingValue;
        description: string;
      }) =>
        termsAPI.updateSetting(
          data.setting_id,
          data.setting_value,
          data.setting_key,
          { description: data.description }
        ),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['settings']);
          toast.success('Terms updated successfully');
          onCloseHandler('terms');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Failed to update terms');
        },
      }
    );

    const deleteSettingMutation = useMutation(
      (settingId: number) => termsAPI.deleteSetting(settingId),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['settings']);
          toast.success('Terms deleted successfully');
        },
        onError: (error: any) => {
          toast.error(error.message || 'Failed to delete terms');
        },
      }
    );

    const handleContentChange = (content: string) => {
      setFormData((prev) => ({
        ...prev,
        setting_value: {
          title: prev.setting_value.title || '',
          content: content,
          html: content,
        },
      }));
    };

    const handleEdit = (
      setting: CategorySetting<RawTermSettingValue, 'terms'>
    ) => {
      setSelectedSetting(setting);
      const termValue = parseTermValue(setting.setting_value);

      setFormData({
        setting_key: setting.setting_key,
        setting_value: termValue,
        description: setting.description || '',
      });
      setShowModal(true);
    };

    const parseTermValue = (settingValue: any): RawTermSettingValue => {
      try {
        if (settingValue?.value?.content || settingValue?.value?.html) {
          return settingValue.value;
        }

        if (typeof settingValue?.value === 'string') {
          try {
            const parsed = JSON.parse(settingValue.value);
            if (typeof parsed === 'string') {
              return JSON.parse(parsed);
            }
            return parsed;
          } catch (e) {
            return {
              title: '',
              content: settingValue.value || '',
              html: settingValue.value || '',
            };
          }
        }

        return {
          title: '',
          content: '',
          html: '',
        };
      } catch (e) {
        console.error('Error parsing term value:', e);
        return {
          title: '',
          content: '',
          html: '',
        };
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (selectedSetting) {
        editSettingMutation.mutate({
          setting_id: selectedSetting.setting_id,
          setting_key: formData.setting_key,
          setting_value: formData.setting_value,
          description: formData.description,
        });
      } else {
        addSettingMutation.mutate(formData);
      }
    };

    const handleDelete = async (
      setting: CategorySetting<RawTermSettingValue, 'terms'>
    ) => {
      if (window.confirm('Are you sure you want to delete these terms?')) {
        await deleteSettingMutation.mutateAsync(setting.setting_id);
      }
    };

    const handleModalClose = () => {
      resetFormState();
      onCloseHandler('terms');
    };

    const renderSettingValue = (
      setting: CategorySetting<RawTermSettingValue, 'terms'>
    ) => {
      const value = parseTermValue(setting.setting_value);
      const htmlContent = value.html || value.content;
      const plainText = htmlContent ? extractTextFromHtml(htmlContent) : '';
      const words = plainText.split(' ').slice(0, 10).join(' ');
      return words ? `${words}...` : '';
    };

    const extractTextFromHtml = (html: string): string => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const text = tempDiv.textContent || tempDiv.innerText;
      return text.replace(/\s+/g, ' ').trim();
    };

    const isLoading =
      isLoadingSettings ||
      addSettingMutation.isLoading ||
      editSettingMutation.isLoading ||
      deleteSettingMutation.isLoading;

    const isFirstTime = !isLoading && !settings?.length;

    return (
      <StyledCard>
        <StyledBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Form.Group className="flex-grow-1 me-3">
              <Form.Control
                type="text"
                placeholder="Search terms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="fs-16"
              />
            </Form.Group>
          </div>

          {isLoading ? (
            <div className="text-center">Loading...</div>
          ) : isFirstTime ? (
            <div className="text-center py-5">
              <p className="mb-4">No terms have been created yet.</p>
              <p>Click the 'Create First Terms' button to get started.</p>
            </div>
          ) : (
            <Table responsive hover className="fs-14">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Content</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {settings?.map((setting) => (
                  <tr key={setting.setting_id}>
                    <td>{setting.setting_key}</td>
                    <td>{renderSettingValue(setting)}</td>
                    <td>{setting.description}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <StyledButton
                          onClick={() => handleEdit(setting)}
                          className="text-primary"
                          variant="link"
                        >
                          Edit
                        </StyledButton>
                        <StyledButton
                          onClick={() => handleDelete(setting)}
                          className="text-danger"
                          variant="link"
                        >
                          Delete
                        </StyledButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </StyledBody>

        <Modal show={showModal} onHide={handleModalClose} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title className="fs-20 fw-600">
              {isFirstTime
                ? 'Create First Terms'
                : selectedSetting
                ? 'Edit Terms'
                : 'Add Terms'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fs-14">Terms Key</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.setting_key}
                  onChange={(e) =>
                    setFormData({ ...formData, setting_key: e.target.value })
                  }
                  required
                  className="fs-16"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fs-14">Content</Form.Label>
                <Tabs
                  activeKey={previewTab}
                  onSelect={(k) => k && setPreviewTab(k)}
                  className="mb-3"
                >
                  <Tab eventKey="edit" title="Edit">
                    <div className="editor-container">
                      <ReactQuill
                        theme="snow"
                        value={formData.setting_value.content}
                        onChange={handleContentChange}
                        modules={EDITOR_MODULES}
                        formats={EDITOR_FORMATS}
                        style={{ height: '300px', marginBottom: '50px' }}
                      />
                    </div>
                  </Tab>
                  <Tab eventKey="preview" title="Preview">
                    <PreviewContainer
                      dangerouslySetInnerHTML={{
                        __html: formData.setting_value.html || '',
                      }}
                      className="preview-content"
                    />
                  </Tab>
                </Tabs>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fs-14">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="fs-16"
                />
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <StyledButton variant="outline" onClick={handleModalClose}>
                  Cancel
                </StyledButton>
                <StyledButton
                  type="submit"
                  disabled={isLoading}
                  className="text-white"
                >
                  {isLoading
                    ? 'Saving...'
                    : isFirstTime
                    ? 'Create'
                    : selectedSetting
                    ? 'Update'
                    : 'Add'}
                </StyledButton>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </StyledCard>
    );
  }
);
TermsCard.displayName = 'TermsCard';

export default TermsCard;
